<template lang="pug">
    .form-container
        .box(v-if='homework')
            div(
                :is='componentName',
                :slug='type',
                :id='id',
                :users-ids='usersIds',
                :homework-id='homeworkId',
                :courseType='courseType',
                ref='form'
            )
                template(v-if="!source || (source !== 'homework-template' && source !== 'lesson-conducting-template')")
                    template(slot='title')
                        template(v-if='usersIds')
                            | {{ $i18n.t('homework.homework_editGame_setIndividualParameters') }}
                        template(v-else) {{ title }}
                    template(v-if='!usersIds', slot='parameters')
                        .settings-header
                            | {{ $i18n.t('homework.homework_editGame_individualParameters') }}
                        template(v-for='index in amountOfIndividualParams')
                            .row(v-if='form.students[index - 1] || availableStudents.length')
                                .box__individual-settings-content
                                    .box__select
                                        cs-multiselect(
                                            :multiple='true',
                                            :options='availableStudents',
                                            :show-labels='false',
                                            :hide-selected='true',
                                            label='fullName',
                                            track-by='id',
                                            :disabled='gameHaveNestedSettings && !isGameInNestedSettingsSelected',
                                            :placeholder="$i18n.t('homework.homework_editGame_individualParametersPlaceholder')",
                                            :value='form.students[index - 1]',
                                            @input='formStudentsHandler($event, index - 1)'
                                        )
                                    .box__buttons(v-if='form.students[index - 1]')
                                        cs-button(
                                            color="blue",
                                            type='button',
                                            @click='editStudents(form.students[index - 1])'
                                        )
                                            | {{ $i18n.t('homework.homework_editGame_set') }}
                                        cs-button.remove-individual-settings-button(
                                            color="blue",
                                            pattern="primary-outline",
                                            @click='removeStudents(index)'
                                        )
                                            svg-icon(name="cancel", size="x-large")
                                .box__individual-parameters(v-if='form.students[index - 1]')
                                    div(v-if='!individualParameters(index)')
                                        | {{ $i18n.t('homework.homework_editGame_setParameters') }}!
                                    div(
                                        v-else,
                                        :is='`${individualSettingsTypePreview}-preview`',
                                        :parameters='individualParameters(index)'
                                    )
                .help-block(slot='help')
                    | {{ courseType === 'mentalArithmetic' ? $i18n.t('common.common_helpText_notAvailableOptions') : '' }}
</template>

<script>
    const MultiplicationAndDivisionForm = () => import('../games/games-forms/MultiplicationAndDivisionForm.vue');
    const MultiplicationAndDivisionPreview = () => import('../games/games-previews/MultDivPreview.vue');
    const MultiplicationAndDivisionMathMagicForm = () =>
        import('../games/games-forms/MultiplicationAndDivisionMathMagicForm');
    const MultiplicationAndDivisionMathMagicPreview = () => import('../games/games-previews/MultDivPreview.vue');
    const TabularMultiplicationForm = () => import('../games/games-forms/TabularMultiplicationForm');
    const TabularMultiplicationPreview = () => import('../games/games-previews/TabularMultiplicationPreview.vue');
    const AssembleTheNumberForm = () => import('../games/games-forms/AssembleTheNumberForm.vue');
    const AssembleTheNumberPreview = () => import('../games/games-previews/AssembleTheNumberPreview.vue');
    const SequenceOfNumbersForm = () => import('../games/games-forms/SequenceOfNumbersForm.vue');
    const SequenceOfNumbersPreview = () => import('../games/games-previews/SequenceOfNumbersPreview.vue');
    const AdditionAndSubtractionForm = () => import('../games/games-forms/AddSubForm.vue');
    const AdditionAndSubtractionPreview = () => import('../games/games-previews/AddSubPreview.vue');
    const AdditionAndSubtractionWithTimeForm = () => import('../games/games-forms/AddSubForm.vue');
    const AdditionAndSubtractionWithTimePreview = () => import('../games/games-previews/AddSubPreview.vue');
    const AdditionAndSubtractionMathMagicForm = () => import('../games/games-forms/AddSubForm.vue');
    const AdditionAndSubtractionMathMagicPreview = () => import('../games/games-previews/AddSubPreview.vue');
    const FlashCardsForm = () => import('../games/games-forms/FlashCardsForm.vue');
    const FlashCardsPreview = () => import('../games/games-previews/FlashCardsPreview.vue');
    const InteractiveFlashCardsForm = () => import('../games/games-forms/FlashCardsForm.vue');
    const InteractiveFlashCardsPreview = () => import('../games/games-previews/FlashCardsPreview.vue');
    const EquationForm = () => import('../games/games-forms/EquationForm.vue');
    const EquationPreview = () => import('../games/games-previews/EquationPreview.vue');
    const FactorizationForm = () => import('../games/games-forms/FactorizationForm.vue');
    const FactorizationPreview = () => import('../games/games-previews/FactorizationPreview.vue');
    const EquationMultiplicationForm = () => import('../games/games-forms/EquationMultiplicationForm.vue');
    const EquationMultiplicationPreview = () => import('../games/games-previews/EquationMultiplicationPreview.vue');
    const AnzanForm = () => import('../games/games-forms/AnzanForm.vue');
    const AnzanPreview = () => import('../games/games-previews/AnzanPreview.vue');
    const PuzzleForm = () => import('../games/games-forms/PuzzleForm.vue');
    const PuzzlePreview = () => import('../games/games-previews/PuzzlePreview.vue');
    const CouplesForm = () => import('../games/games-forms/CouplesForm.vue');
    const CouplesPreview = () => import('../games/games-previews/CouplesPreview.vue');
    const DiadaForm = () => import('../games/games-forms/DiadaForm.vue');
    const DiadaPreview = () => import('../games/games-previews/DiadaPreview.vue');
    const ChainsPreview = () => import('../games/games-previews/ChainsPreview.vue');
    const ChainsForm = () => import('../games/games-forms/ChainsForm.vue');
    const SchulteForm = () => import('../games/games-forms/SchulteForm.vue');
    const SchultePreview = () => import('../games/games-previews/SchultePreview.vue');
    const ScannerPreview = () => import('../games/games-previews/ScannerPreview.vue');
    const ScannerForm = () => import('../games/games-forms/ScannerForm.vue');
    const ShutterForm = () => import('../games/games-forms/ShutterForm.vue');
    const ShutterPreview = () => import('../games/games-previews/ShutterPreview.vue');
    const ShutterReadingEnForm = () => import('../games/games-forms/ShutterReadingEnForm.vue');
    const ShutterReadingEnPreview = () => import('../games/games-previews/ShutterReadingEnPreview.vue');
    const ShutterRussianLanguageForm = () => import('../games/games-forms/ShutterRussianLanguageForm.vue');
    const ShutterRussianLanguagePreview = () => import('../games/games-previews/ShutterRussianLanguagePreview.vue');
    const WordsFieldPreview = () => import('../games/games-previews/WordsFieldPreview.vue');
    const WordsFieldForm = () => import('../games/games-forms/WordsFieldForm.vue');
    const WordsFieldReadingEnPreview = () => import('../games/games-previews/WordsFieldReadingEnPreview.vue');
    const WordsFieldReadingEnForm = () => import('../games/games-forms/WordsFieldReadingEnForm.vue');
    const AnagramsForm = () => import('../games/games-forms/AnagramsForm.vue');
    const AnagramsPreview = () => import('../games/games-previews/AnagramsPreview.vue');
    const TextsForm = () => import('../games/games-forms/TextsForm.vue');
    const TextsPreview = () => import('../games/games-previews/TextsPreview.vue');
    const LettersFieldPreview = () => import('../games/games-previews/LettersFieldPreview.vue');
    const LettersFieldForm = () => import('../games/games-forms/LettersFieldForm.vue');
    const CatchWordForm = () => import('../games/games-forms/CatchWordForm.vue');
    const CatchWordPreview = () => import('../games/games-previews/CatchWordPreview.vue');
    const CatchWordReadingEnForm = () => import('../games/games-forms/CatchWordReadingEnForm.vue');
    const CatchWordReadingEnPreview = () => import('../games/games-previews/CatchWordReadingEnPreview.vue');
    const VisionFieldForm = () => import('../games/games-forms/VisionFieldForm.vue');
    const VisionFieldPreview = () => import('../games/games-previews/VisionFieldPreview.vue');
    const VisionFieldReadingEnForm = () => import('../games/games-forms/VisionFieldReadingEnForm.vue');
    const VisionFieldReadingEnPreview = () => import('../games/games-previews/VisionFieldReadingEnPreview.vue');
    const MatrixForm = () => import('../games/games-forms/MatrixForm.vue');
    const MatrixPreview = () => import('../games/games-previews/MatrixPreview.vue');
    const MemorizeForm = () => import('../games/games-forms/MemorizeForm.vue');
    const MemorizePreview = () => import('../games/games-previews/MemorizePreview.vue');
    const KeenEyeForm = () => import('../games/games-forms/KeenEyeForm.vue');
    const KeenEyePreview = () => import('../games/games-previews/KeenEyePreview.vue');
    const KeenEyeBonusGamesForm = () => import('../games/games-forms/KeenEyeForm.vue');
    const KeenEyeBonusGamesPreview = () => import('../games/games-previews/KeenEyePreview.vue');
    const NumericalNetworkForm = () => import('../games/games-forms/NumericalNetworkForm.vue');
    const NumericalNetworkPreview = () => import('../games/games-previews/NumericalNetworkPreview.vue');
    const NavigatorForm = () => import('../games/games-forms/NavigatorForm.vue');
    const NavigatorPreview = () => import('../games/games-previews/NavigatorPreview.vue');
    const NavigatorBonusGamesForm = () => import('../games/games-forms/NavigatorForm.vue');
    const NavigatorBonusGamesPreview = () => import('../games/games-previews/NavigatorPreview.vue');
    const SynonymsForm = () => import('../games/games-forms/AntonymsForm.vue');
    const SynonymsPreview = () => import('../games/games-previews/SynonymsPreview.vue');
    const SynonymsRussianLanguageForm = () => import('../games/games-forms/SynonymsRussianLanguageForm.vue');
    const SynonymsRussianLanguagePreview = () => import('../games/games-previews/SynonymsRussianLanguagePreview.vue');
    const SynonymsReadingEnForm = () => import('../games/games-forms/SynonymsReadingEnForm.vue');
    const SynonymsReadingEnPreview = () => import('../games/games-previews/SynonymsReadingEnPreview.vue');
    const AntonymsForm = () => import('../games/games-forms/AntonymsForm.vue');
    const AntonymsPreview = () => import('../games/games-previews/AntonymsPreview.vue');
    const AntonymsRussianLanguageForm = () => import('../games/games-forms/AntonymsRussianLanguageForm.vue');
    const AntonymsRussianLanguagePreview = () => import('../games/games-previews/AntonymsRussianLanguagePreview.vue');
    const ClassificationsForm = () => import('../games/games-forms/ClassificationsForm.vue');
    const ClassificationsPreview = () => import('../games/games-previews/Classifications.vue');
    const SoundCouplesForm = () => import('../games/games-forms/SoundCouplesForm.vue');
    const SoundCouplesPreview = () => import('../games/games-previews/SoundCouples.vue');
    const FourthOneForm = () => import('../games/games-forms/FourthOneForm.vue');
    const FourthOnePreview = () => import('../games/games-previews/FourthOne.vue');
    const SearchForm = () => import('../games/games-forms/SearchForm.vue');
    const SearchPreview = () => import('../games/games-previews/Search.vue');
    const EquationOnlineForm = () => import('../games/games-forms/EquationOnlineForm.vue');
    const EquationOnlinePreview = () => import('../games/games-previews/EquationOnlinePreview.vue');
    const DigitCouplesForm = () => import('../games/games-forms/DigitCouplesForm.vue');
    const DigitCouplesPreview = () => import('../games/games-previews/DigitCouplesPreview.vue');
    const MemoryForm = () => import('../games/games-forms/MemoryForm.vue');
    const MemoryPreview = () => import('../games/games-previews/MemoryPreview.vue');
    const ExpressionsForm = () => import('../games/games-forms/ExpressionsForm.vue');
    const RootsAndDegreesForm = () => import('../games/games-forms/RootsAndDegreesForm.vue');
    const RootsAndDegreesPreview = () => import('../games/games-previews/RootsAndDegreesPreview.vue');
    const RootsAndSquaresForm = () => import('../games/games-forms/RootsAndSquaresForm.vue');
    const RootsAndSquaresPreview = () => import('../games/games-previews/RootsAndSquaresPreview.vue');
    const DayOfWeekForm = () => import('../games/games-forms/DayOfWeekForm.vue');
    const DayOfWeekPreview = () => import('../games/games-previews/DayOfWeekPreview.vue');
    const LonerForm = () => import('../games/games-forms/LonerForm');
    const LonerPreview = () => import('../games/games-previews/LonerPreview');
    const LonerBonusGamesForm = () => import('../games/games-forms/LonerBonusGamesForm');
    const LonerBonusGamesPreview = () => import('../games/games-previews/LonerBonusGamesPreview');
    const CouplesMemoryDevelopmentForm = () => import('../games/games-forms/CouplesForm.vue');
    const CouplesMemoryDevelopmentPreview = () => import('../games/games-previews/CouplesPreview.vue');
    const MemoForm = () => import('../games/games-forms/MemoForm.vue');
    const MemoPreview = () => import('../games/games-previews/MemoPreview.vue');
    const MemoBonusGamesForm = () => import('../games/games-forms/MemoForm.vue');
    const MemoBonusGamesPreview = () => import('../games/games-previews/MemoPreview.vue');
    const MemoryXForm = () => import('../games/games-forms/MemoryXForm.vue');
    const MemoryXPreview = () => import('../games/games-previews/MemoryXPreview.vue');
    const RevivedNumbersForm = () => import('../games/games-forms/RevivedNumbersForm.vue');
    const RevivedNumbersPreview = () => import('../games/games-previews/RevivedNumbersPreview.vue');
    const AssociationsForm = () => import('../games/games-forms/AssociationsForm.vue');
    const AssociationsPreview = () => import('../games/games-previews/AssociationsPreview.vue');
    const WhoIsWhoForm = () => import('../games/games-forms/WhoIsWhoForm.vue');
    const WhoIsWhoPreview = () => import('../games/games-previews/WhoIsWhoPreview.vue');
    const CosmoTourForm = () => import('../games/games-forms/CosmoTourForm.vue');
    const CosmoTourPreview = () => import('../games/games-previews/CosmoTourPreview.vue');
    const SecretsOfCiceroForm = () => import('../games/games-forms/SecretsOfCiceroForm.vue');
    const SecretsOfCiceroPreview = () => import('../games/games-previews/SecretsOfCiceroPreview.vue');
    const VocabularyWordsForm = () => import('../games/games-forms/VocabularyWordsForm.vue');
    const VocabularyWordsPreview = () => import('../games/games-previews/VocabularyWordsPreview.vue');
    const VocabularyMemoForm = () => import('../games/games-forms/VocabularyMemoForm.vue');
    const VocabularyMemoPreview = () => import('../games/games-previews/VocabularyMemoPreview');
    const RepeatAfterMeForm = () => import('../games/games-forms/RepeatAfterMeForm.vue');
    const RepeatAfterMePreview = () => import('../games/games-previews/RepeatAfterMePreview.vue');
    const ChameleonForm = () => import('../games/games-forms/ChameleonForm.vue');
    const ChameleonPreview = () => import('../games/games-previews/ChameleonPreview.vue');
    const MentalDrawingForm = () => import('../games/games-forms/MentalDrawingForm.vue');
    const MentalDrawingPreview = () => import('../games/games-previews/MentalDrawingPreview.vue');
    const HandsUpForm = () => import('../games/games-forms/HandsUpForm.vue');
    const HandsUpPreview = () => import('../games/games-previews/HandsUpPreview.vue');
    const CorrectiveSamplesForm = () => import('../games/games-forms/CorrectiveSamplesForm.vue');
    const CorrectiveSamplesPreview = () => import('../games/games-previews/CorrectiveSamplesPreview.vue');
    const ChainsBonusGameForm = () => import('../games/games-forms/ChainsBonusGameForm.vue');
    const ChainsBonusGamePreview = () => import('../games/games-previews/ChainsBonusGamePreview.vue');
    const ScoutForm = () => import('../games/games-forms/ScoutForm.vue');
    const ScoutPreview = () => import('../games/games-previews/ScoutPreview.vue');
    const AlpariForm = () => import('../games/games-forms/AlpariForm.vue');
    const AlpariPreview = () => import('../games/games-previews/AlpariPreview.vue');
    const ChainsReadingEnForm = () => import('../games/games-forms/ChainsReadingEnForm.vue');
    const ChainsReadingEnPreview = () => import('../games/games-previews/ChainsReadingEnPreview.vue');
    const CouplesReadingEnForm = () => import('../games/games-forms/CouplesReadingEnForm.vue');
    const CouplesReadingEnPreview = () => import('../games/games-previews/CouplesReadingEnPreview.vue');
    const DivideItRightForm = () => import('../games/games-forms/DivideItRightForm.vue');
    const DivideItRightPreview = () => import('../games/games-previews/DivideItRightPreview.vue');
    const ChooseItRightForm = () => import('../games/games-forms/ChooseItRightForm.vue');
    const ChooseItRightPreview = () => import('../games/games-previews/ChooseItRightPreview.vue');
    const DivideUnstressedVowelsForm = () => import('../games/games-forms/DivideUnstressedVowelsForm.vue');
    const DivideUnstressedVowelsPreview = () => import('../games/games-previews/DivideUnstressedVowelsPreview.vue');
    const WordStructureForm = () => import('../games/games-forms/WordStructureForm.vue');
    const WordStructurePreview = () => import('../games/games-previews/WordStructurePreview.vue');
    const SentenceParsingForm = () => import('../games/games-forms/SentenceParsingForm.vue');
    const SentenceParsingPreview = () => import('../games/games-previews/SentenceParsingPreview.vue');
    const VocabularyWordsRusForm = () => import('../games/games-forms/VocabularyWordsRusForm.vue');
    const VocabularyWordsRusPreview = () => import('../games/games-previews/VocabularyWordsRusPreview.vue');
    const LettersSoundsSyllablesForm = () => import('../games/games-forms/LettersSoundsSyllablesForm.vue');
    const LettersSoundsSyllablesPreview = () => import('../games/games-previews/LettersSoundsSyllablesPreview.vue');
    const TransferSyllablesForm = () => import('../games/games-forms/TransferSyllablesForm.vue');
    const TransferSyllablesPreview = () => import('../games/games-previews/TransferSyllablesPreview.vue');
    const TransferSyllablesSchoolPreparingForm = () => import('../games/games-forms/TransferSyllablesSchoolPreparingForm.vue');
    const TransferSyllablesSchoolPreparingPreview = () => import('../games/games-previews/TransferSyllablesSchoolPreparingPreview.vue');
    const HardSoftConsonantsForm = () => import('../games/games-forms/HardSoftConsonantsForm.vue');
    const HardSoftConsonantsPreview = () => import('../games/games-previews/HardSoftConsonantsPreview.vue');
    const HardSoftConsonantsSchoolPreparingForm = () => import('../games/games-forms/HardSoftConsonantsSchoolPreparingForm.vue');
    const HardSoftConsonantsSchoolPreparingPreview = () => import('../games/games-previews/HardSoftConsonantsSchoolPreparingPreview.vue');
    const VocabularyMemoRusForm = () => import('../games/games-forms/VocabularyMemoRusForm.vue');
    const VocabularyMemoRusPreview = () => import('../games/games-previews/VocabularyMemoRusPreview.vue');
    const WordDrumForm = () => import('../games/games-forms/WordDrumForm.vue');
    const WordDrumPreview = () => import('../games/games-previews/WordDrumPreview.vue');
    const OrthographForm = () => import('../games/games-forms/OrthographForm.vue');
    const OrthographPreview = () => import('../games/games-previews/OrthographPreview.vue');
    const TranscriptionInWordForm = () => import('../games/games-forms/TranscriptionInWordForm.vue');
    const TranscriptionInWordPreview = () => import('../games/games-previews/TranscriptionInWordPreview.vue');
    const VoicedAndDeafConsonantsForm = () => import('../games/games-forms/VoicedAndDeafConsonantsForm.vue');
    const VoicedAndDeafConsonantsPreview = () => import('../games/games-previews/VoicedAndDeafConsonantsPreview.vue');
    const PartsOfSpeechForm = () => import('../games/games-forms/PartsOfSpeechForm.vue');
    const PartsOfSpeechPreview = () => import('../games/games-previews/PartsOfSpeechPreview.vue');
    const ParsingSentencePartsSpeechForm = () => import('../games/games-forms/ParsingSentencePartsSpeechForm.vue');
    const ParsingSentencePartsSpeechPreview = () => import('../games/games-previews/ParsingSentencePartsSpeechPreview.vue');
    const SortOneManyForm = () => import('../games/games-forms/SortOneManyForm.vue');
    const SortOneManyPreview = () => import('../games/games-previews/SortOneManyPreview.vue');
    const GenericWordsForm = () => import('../games/games-forms/GenericWordsForm.vue');
    const GenericWordsPreview = () => import('../games/games-previews/GenericWordsPreview.vue');
    const VerbTensesForm = () => import('../games/games-forms/VerbTensesForm.vue');
    const VerbTensesPreview = () => import('../games/games-previews/VerbTensesPreview.vue');
    const PunctuationMarksForm = () => import('../games/games-forms/PunctuationMarksForm.vue');
    const PunctuationMarksPreview = () => import('../games/games-previews/PunctuationMarksPreview.vue');
    const AlphabetForm = () => import('../games/games-forms/AlphabetForm.vue');
    const AlphabetPreview = () => import('../games/games-previews/AlphabetPreview.vue');
    const ConnectThePuzzlesForm = () => import('../games/games-forms/ConnectThePuzzlesForm.vue');
    const ConnectThePuzzlesPreview = () => import('../games/games-previews/ConnectThePuzzlesPreview.vue');

    import _ from 'lodash';
    import { mapState } from 'vuex';

    const NAMES_OF_GAMES = ['Puzzle'];
    export default {
        props: ['id', 'usersIds', 'homeworkId', 'source', 'individualGeneratorSettings', 'courseType'],
        data() {
            return {
                type: null,
                action: null,
                students: [],
                form: {
                    students: [],
                },
                amountOfIndividualParams: 1,
            };
        },
        created() {
            if (!this.homework && !this.course) {
                this.cancel();
                return false;
            } else if (this.homework) {
                if (!this.homeworkCopy) {
                    this.$store.dispatch('setHomeworkCopy', _.cloneDeep(this.homework));
                }
                this.type = this.homework.games[this.id].slug;
            } else {
                this.type = this.course.games[this.id].slug;
            }
            this.students = this.homeworkStudents;
            this.setIndividualStudents();
        },
        methods: {
            cancel() {
                if (this.homeworkId) {
                    this.$router.push({ name: 'homework:edit', params: { id: this.homeworkId, type: 'homework' } });
                } else {
                    this.$router.push({ name: 'homework:create', params: { type: 'homework' } });
                }
            },
            formStudentsHandler(event, index) {
                if (event.length === 0) {
                    this.form.students.splice(index, 1);
                } else {
                    this.$set(this.form.students, index, event);
                }
            },
            setIndividualStudents() {
                if (this.homework) {
                    if (this.gameHaveNestedSettings && !this.isGameInNestedSettingsSelected) {
                        return;
                    }
                    let handledParams = this.gameHaveNestedSettings
                        ? this.homework.games[this.id].parameters.default.games[0].parameters
                        : this.homework.games[this.id].parameters;
                    for (let key in handledParams) {
                        if (key !== 'default') {
                            let studentsWithParams = [];
                            this.students.forEach(student => {
                                if (key.match(/[\d]*[\d]+/g).some(userId => student.id === +userId)) {
                                    student.parameters = handledParams[key];
                                    if (this.gameHaveNestedSettings && !this.isGameInNestedSettingsSelected) {
                                        return;
                                    }
                                    studentsWithParams.push(student);
                                }
                            });
                            this.form.students.push(studentsWithParams);
                        }
                    }
                }
            },
            editStudents(students) {
                let routerParams = {
                    gameId: this.id,
                    usersIds: students.map(student => student.id).join(','),
                    id: this.homeworkId,
                };
                if (NAMES_OF_GAMES.includes(this.type)) {
                    let key = 'usersU' + students.map(student => student.id).join('U');
                    let homeworkCopy = _.cloneDeep(this.homework);
                    let typesOfExamples = homeworkCopy.games[this.id].parameters.default.games;
                    if (!typesOfExamples[0].parameters[key]) {
                        typesOfExamples[0].parameters[key] = _.cloneDeep(typesOfExamples[0].parameters.default);
                    }
                    this.$store.dispatch('setHomework', homeworkCopy);
                    if (this.$refs.form.courseGames) {
                        let gamesParameters = _.cloneDeep(this.gamesParameters);
                        let game = _.find(this.$refs.form.courseGames, {
                            slug: this.homework.games[this.id].parameters.default.games[0].slug,
                        });
                        gamesParameters[game.slug] = game.parameters.default;
                        this.$store.dispatch('setGamesParameters', _.cloneDeep(gamesParameters));
                    }
                    routerParams.individualGeneratorSettings = 'individualGeneratorSettings';
                } else {
                    this.$refs.form.setGameInStorage();
                }
                this.$router.push({ name: 'game:edit', params: routerParams });
            },
            removeStudents(index) {
                this.form.students.splice(index - 1, 1);
            },
            individualParameters(index) {
                if (this.form.students[index - 1].filter(student => student && !student.parameters).length) {
                    return false;
                } else if (this.form.students[index - 1][0]) {
                    return this.form.students[index - 1][0].parameters;
                }
                return false;
            },
        },
        beforeRouteLeave(to, from, next) {
            to.params.type = this.prevRouteType;
            let allowedPaths = [
                'game:edit',
                'homework:create',
                'homework:edit',
                'homework-template:create',
                'homework-template:edit',
                'lesson-conducting-template:create',
                'lesson-conducting-template:edit',
                'create:game',
            ];
            if (!allowedPaths.includes(to.name)) {
                this.$store.dispatch('setHomework', null);
                this.$store.dispatch('setHomeworkCopy', null);
                this.$store.dispatch('setPuzzleGame', null);
                this.$store.dispatch('setGamesParameters', null);
            } else if (to.name === 'homework:edit' || to.name === 'homework:create') {
                if (this.homeworkCopy) {
                    this.$store.dispatch('setHomework', _.cloneDeep(this.homeworkCopy));
                    this.$store.dispatch('setHomeworkCopy', null);
                }
            }
            next();
        },
        computed: {
            ...mapState({
                homework: state => state.trainer.homework,
                homeworkCopy: state => state.trainer.homeworkCopy,
                gamesParameters: state => state.trainer.gamesParameters,
                prevRouteType: state => state.router.previousRoute.params.type,
            }),
            availableStudents() {
                let students = this.students;
                if (students && this.homework && !this.homework.editable) {
                    students = students.filter(student => {
                        return !student.pivot || !student.pivot.isCompleted;
                    });
                }
                return _.differenceBy(students, _.flatten(this.form.students), 'id');
            },
            homeworkStudents() {
                let students = this.homework.students;
                if (students && students.length) {
                    students = students.map(student => {
                        if (student.parameters && student.parameters.game !== this.type) {
                            student.parameters = null;
                        }
                        return student;
                    });
                }
                return students;
            },
            title() {
                switch (this.type) {
                    case 'AssembleTheNumber':
                        return this.$i18n.t('common.common_gameName_assembleTheNumber');
                    case 'MultiplicationAndDivision':
                        return this.$i18n.t('common.common_gameName_multiplicationAndDivision');
                    case 'MultiplicationAndDivisionMathMagic':
                        return this.$i18n.t('common.common_gameName_multiplicationAndDivisionMathMagic');
                    case 'TabularMultiplication':
                        return this.$i18n.t('common.common_gameName_tabularMultiplication');
                    case 'AdditionAndSubtraction':
                        return this.$i18n.t('common.common_gameName_additionAndSubtraction');
                    case 'AdditionAndSubtractionWithTime':
                        return this.$i18n.t('common.common_gameName_additionAndSubtractionWithTime');
                    case 'AdditionAndSubtractionMathMagic':
                        return this.$i18n.t('common.common_gameName_additionAndSubtractionMathMagic');
                    case 'SequenceOfNumbers':
                        return this.$i18n.t('common.common_gameName_sequenceOfNumbers');
                    case 'FlashCards':
                        return this.$i18n.t('common.common_gameName_flashCards');
                    case 'InteractiveFlashCards':
                        return this.$i18n.t('common.common_gameName_interactiveFlashCards');
                    case 'Equation':
                        return this.$i18n.t('common.common_gameName_equation');
                    case 'Factorization':
                        return this.$i18n.t('common.common_gameName_factorization');
                    case 'Anzan':
                        return this.$i18n.t('common.common_gameName_anzan');
                    case 'Puzzle':
                        return this.$i18n.t('common.common_gameName_puzzle');
                    case 'Expressions':
                        return this.$i18n.t('common.common_gameName_expressions');
                    case 'Couples':
                    case 'CouplesReadingEn':
                        return this.$i18n.t('common.common_gameName_couples');
                    case 'Chains':
                    case 'ChainsMemoryDevelopment':
                    case 'ChainsReadingEn':
                        return this.$i18n.t('common.common_gameName_chains');
                    case 'Scanner':
                    case 'ScannerReadingEn':
                        return this.$i18n.t('common.common_gameName_scanner');
                    case 'Shutter':
                    case 'ShutterReadingEn':
                        return this.$i18n.t('common.common_gameName_shutter');
                    case 'ShutterRussianLanguage':
                        return this.$i18n.t('common.common_gameName_shutterRussianLanguage');
                    case 'Schulte':
                    case 'SchulteReadingEn':
                        return this.$i18n.t('common.common_gameName_schulte');
                    case 'WordsField':
                    case 'WordsFieldReadingEn':
                        return this.$i18n.t('common.common_gameName_wordsField');
                    case 'Anagrams':
                        return this.$i18n.t('common.common_gameName_anagrams');
                    case 'Texts':
                        return this.$i18n.t('common.common_gameName_texts');
                    case 'LettersField':
                    case 'LettersFieldReadingEn':
                        return this.$i18n.t('common.common_gameName_lettersField');
                    case 'CatchWord':
                    case 'CatchWordReadingEn':
                        return this.$i18n.t('common.common_gameName_catchWord');
                    case 'VisionField':
                    case 'VisionFieldReadingEn':
                        return this.$i18n.t('common.common_gameName_visionField');
                    case 'RootsAndDegrees':
                        return this.$i18n.t('common.common_gameName_rootsAndDegrees');
                    case 'RootsAndSquares':
                        return this.$i18n.t('common.common_gameName_rootsAndSquares');
                    case 'Matrix':
                        return this.$i18n.t('common.common_gameName_matrix');
                    case 'Navigator':
                    case 'NavigatorMemoryDevelopment':
                    case 'NavigatorBonusGames':
                        return this.$i18n.t('common.common_gameName_navigator');
                    case 'Synonyms':
                    case 'SynonymsReadingEn':
                    case 'SynonymsRussianLanguage':
                        return this.$i18n.t('common.common_gameName_synonyms');
                    case 'SynonymsOnline':
                        return this.$i18n.t('common.common_gameName_synonymsOnline');
                    case 'TextsOnline':
                        return this.$i18n.t('common.common_gameName_textsOnline');
                    case 'Antonyms':
                    case 'AntonymsRussianLanguage':
                        return this.$i18n.t('common.common_gameName_antonyms');
                    case 'Classifications':
                        return this.$i18n.t('common.common_gameName_classifications');
                    case 'SoundCouples':
                        return this.$i18n.t('common.common_gameName_soundCouples');
                    case 'FourthOne':
                        return this.$i18n.t('common.common_gameName_fourthOne');
                    case 'Search':
                        return this.$i18n.t('common.common_gameName_search');
                    case 'ShutterOnline':
                        return this.$i18n.t('common.common_gameName_shutterOnline');
                    case 'AnagramsOnline':
                        return this.$i18n.t('common.common_gameName_anagramsOnline');
                    case 'EquationOnline':
                        return this.$i18n.t('common.common_gameName_equationOnline');
                    case 'CatchWordOnline':
                        return this.$i18n.t('common.common_gameName_catchWordOnline');
                    case 'DigitCouples':
                        return this.$i18n.t('common.common_gameName_digitCouples');
                    case 'Memory':
                        return this.$i18n.t('common.common_gameName_memory');
                    case 'DayOfWeek':
                        return this.$i18n.t('common.common_gameName_dayOfWeek');
                    case 'Memorize':
                        return this.$i18n.t('common.common_gameName_memorize');
                    case 'KeenEye':
                    case 'KeenEyeMemoryDevelopment':
                    case 'KeenEyeBonusGames':
                        return this.$i18n.t('common.common_gameName_keenEye');
                    case 'NumericalNetwork':
                        return this.$i18n.t('common.common_gameName_numericalNetwork');
                    case 'CouplesMemoryDevelopment':
                        return this.$i18n.t('common.common_gameName_couples');
                    case 'Memo':
                    case 'MemoBonusGames':
                        return this.$i18n.t('common.common_gameName_memo');
                    case 'MemoryX':
                        return this.$i18n.t('common.common_gameName_memoryX');
                    case 'RevivedNumbers':
                        return this.$i18n.t('common.common_gameName_revivedNumbers');
                    case 'Associations':
                        return this.$i18n.t('common.common_gameName_associations');
                    case 'WhoIsWho':
                        return this.$i18n.t('common.common_gameName_whoIsWho');
                    case 'Loner':
                    case 'LonerMemoryDevelopment':
                    case 'LonerBonusGames':
                        return this.$i18n.t('common.common_gameName_loner');
                    case 'SecretsOfCicero':
                        return this.$i18n.t('common.common_gameName_secretsOfCicero');
                    case 'VocabularyWords':
                        return this.$i18n.t('common.common_gameName_vocabularyWords');
                    case 'VocabularyMemo':
                        return this.$i18n.t('common.common_gameName_vocabularyMemo');
                    case 'CosmoTour':
                        return this.$i18n.t('common.common_gameName_cosmoTour');
                    case 'RepeatAfterMe':
                        return this.$i18n.t('common.common_gameName_repeatAfterMe');
                    case 'Chameleon':
                        return this.$i18n.t('common.common_gameName_chameleon');
                    case 'MentalDrawing':
                        return this.$i18n.t('common.common_gameName_mentalDrawing');
                    case 'HandsUp':
                        return this.$i18n.t('common.common_gameName_handsUp');
                    case 'Diada':
                        return this.$i18n.t('common.common_gameName_diada');
                    case 'CorrectiveSamples':
                    case 'CorrectiveSamplesWithTime':
                        return this.$i18n.t('common.common_gameName_correctiveSamples');
                    case 'Scout':
                        return this.$i18n.t('common.common_gameName_scout');
                    case 'Alpari':
                        return this.$i18n.t('common.common_gameName_alpari');
                    case 'DivideItRight':
                        return this.$i18n.t('common.common_gameName_divideItRight');
                    case 'ChooseItRight':
                        return this.$i18n.t('common.common_gameName_chooseItRight');
                    case 'DivideUnstressedVowels':
                        return this.$i18n.t('common.common_gameName_divideUnstressedVowels');
                    case 'WordStructure':
                        return this.$i18n.t('common.common_gameName_wordStructure');
                    case 'SentenceParsing':
                        return this.$i18n.t('common.common_gameName_sentenceParsing');
                    case 'VocabularyWordsRus':
                        return this.$i18n.t('common.common_gameName_vocabularyWordsRus');
                    case 'LettersSoundsSyllables':
                        return this.$i18n.t('common.common_gameName_lettersSoundsSyllables');
                    case 'TransferSyllables':
                        return this.$i18n.t('common.common_gameName_transferSyllables');
                    case 'TransferSyllablesSchoolPreparing':
                        return this.$i18n.t('common.common_gameName_transferSyllablesSchoolPreparing');
                    case 'HardSoftConsonants':
                        return this.$i18n.t('common.common_gameName_hardSoftConsonants');
                    case 'HardSoftConsonantsSchoolPreparing':
                        return this.$i18n.t('common.common_gameName_hardSoftConsonantsSchoolPreparing');
                    case 'VocabularyMemoRus':
                        return this.$i18n.t('common.common_gameName_vocabularyMemoRus');
                    case 'WordDrum':
                        return this.$i18n.t('common.common_gameName_wordDrum');
                    case 'TranscriptionInWord':
                        return this.$i18n.t('common.common_gameName_transcriptionInWord');
                    case 'VoicedAndDeafConsonants':
                        return this.$i18n.t('common.common_gameName_voicedAndDeafConsonants');
                    case 'PartsOfSpeech':
                        return this.$i18n.t('common.common_gameName_partsOfSpeech');
                    case 'ParsingSentencePartsSpeech':
                        return this.$i18n.t('common.common_gameName_parsingSentencePartsSpeech');
                    case 'SortOneMany':
                        return this.$i18n.t('common.common_gameName_sortOneMany');
                    case 'GenericWords':
                        return this.$i18n.t('common.common_gameName_genericWords');
                    case 'VerbTenses':
                        return this.$i18n.t('common.common_gameName_verbTenses');
                    case 'PunctuationMarks':
                        return this.$i18n.t('common.common_gameName_punctuationMarks');
                    case 'Alphabet':
                        return this.$i18n.t('common.common_gameName_alphabet');
                    case 'ConnectThePuzzles':
                        return this.$i18n.t('common.common_gameName_connectThePuzzles');
                    case 'Orthograph':
                        return this.$i18n.t('common.common_gameName_orthograph');
                    default:
                        return '';
                }
            },
            gameHaveNestedSettings() {
                if (this.homework) {
                    return Boolean(
                        this.homework.games[this.id].parameters.default &&
                            this.homework.games[this.id].parameters.default.games
                    );
                }
                return false;
            },
            isGameInNestedSettingsSelected() {
                if (this.homework) {
                    return Boolean(
                        this.gameHaveNestedSettings && this.homework.games[this.id].parameters.default.games.length
                    );
                }
                return false;
            },
            individualSettingsTypePreview() {
                if (this.homework) {
                    let previewString =
                        this.gameHaveNestedSettings && this.isGameInNestedSettingsSelected
                            ? this.homework.games[this.id].parameters.default.games[0].slug
                            : this.type;
                    if (previewString.includes('Online') && previewString !== 'EquationOnline') {
                        return previewString.substring(0, previewString.length - 'Online'.length);
                    }
                    return previewString;
                }
                return '';
            },
            componentName() {
                if (this.type.includes('Online') && this.type !== 'EquationOnline') {
                    return `${this.type.substring(0, this.type.length - 'Online'.length)}-form`;
                }
                return `${this.type}-form`;
            },
        },
        watch: {
            'form.students': {
                handler(val) {
                    if (this.students && _.flatten(val).length === this.students.length) {
                        this.amountOfIndividualParams = val.length;
                    } else if (!this.students || _.flatten(val).length !== this.students.length) {
                        this.amountOfIndividualParams = val.length + 1;
                    }
                    if (this.gameHaveNestedSettings && !this.isGameInNestedSettingsSelected) {
                        return;
                    }
                    let handledParams = this.gameHaveNestedSettings
                        ? this.homework.games[this.id].parameters.default.games[0].parameters
                        : this.homework.games[this.id].parameters;
                    for (let key in handledParams) {
                        if (key === 'default') {
                            continue;
                        }
                        let studentsIdsFromKey = key.match(/[\d]*[\d]+/g).map(userId => +userId);
                        let isKeyExist = val.some(
                            students =>
                                _.difference(
                                    studentsIdsFromKey,
                                    students.map(student => student.id)
                                ).length === 0 &&
                                _.difference(
                                    students.map(student => student.id),
                                    studentsIdsFromKey
                                ).length === 0
                        );
                        if (!isKeyExist) {
                            delete handledParams[key];
                            let cancelledStudents = this.homework.students.filter(student =>
                                studentsIdsFromKey.some(userId => userId === student.id)
                            );
                            if (cancelledStudents) {
                                cancelledStudents.forEach(student => delete student.parameters);
                                this.students.forEach(student => {
                                    if (
                                        cancelledStudents.some(cancelledStudent => cancelledStudent.id === student.id)
                                    ) {
                                        delete student.parameters;
                                    }
                                });
                            }
                        }
                    }
                },
                deep: true,
            },
            $route(to, from) {
                if (
                    this.individualGeneratorSettings &&
                    this.individualGeneratorSettings === 'individualGeneratorSettings'
                ) {
                    this.type = this.homework.games[this.id].parameters.default.games[0].slug;
                    return;
                }
                if (
                    from.name === 'game:edit' &&
                    to.name === 'game:edit' &&
                    from.params.individualGeneratorSettings === 'individualGeneratorSettings'
                ) {
                    this.type = this.homework.games[this.id].slug;
                    this.form.students = [];
                    this.setIndividualStudents();
                    return;
                }
                if (from.path.match(/[\d]*[\d]+/g)) {
                    this.form.students = [];
                    this.setIndividualStudents();
                }
            },
            isGameInNestedSettingsSelected(val) {
                if (!val) {
                    this.form.students = [];
                    if (this.students) {
                        this.students.forEach(student => {
                            delete student.parameters;
                        });
                    }
                }
            },
        },
        components: {
            EquationMultiplicationForm,
            EquationMultiplicationPreview,
            MultiplicationAndDivisionForm,
            MultiplicationAndDivisionPreview,
            MultiplicationAndDivisionMathMagicForm,
            MultiplicationAndDivisionMathMagicPreview,
            TabularMultiplicationForm,
            TabularMultiplicationPreview,
            SequenceOfNumbersForm,
            SequenceOfNumbersPreview,
            AssembleTheNumberForm,
            AssembleTheNumberPreview,
            AdditionAndSubtractionForm,
            AdditionAndSubtractionPreview,
            AdditionAndSubtractionWithTimeForm,
            AdditionAndSubtractionWithTimePreview,
            AdditionAndSubtractionMathMagicForm,
            AdditionAndSubtractionMathMagicPreview,
            FlashCardsForm,
            FlashCardsPreview,
            InteractiveFlashCardsForm,
            InteractiveFlashCardsPreview,
            EquationForm,
            FactorizationForm,
            EquationPreview,
            FactorizationPreview,
            AnzanForm,
            AnzanPreview,
            PuzzlePreview,
            PuzzleForm,
            RootsAndDegreesPreview,
            RootsAndDegreesForm,
            RootsAndSquaresPreview,
            RootsAndSquaresForm,
            CouplesForm,
            CouplesPreview,
            ChainsPreview,
            ChainsForm,
            SchulteForm,
            SchultePreview,
            ScannerPreview,
            ExpressionsForm,
            ScannerForm,
            ShutterPreview,
            ShutterForm,
            ShutterReadingEnPreview,
            ShutterReadingEnForm,
            ShutterRussianLanguageForm,
            ShutterRussianLanguagePreview,
            WordsFieldPreview,
            WordsFieldForm,
            WordsFieldReadingEnPreview,
            WordsFieldReadingEnForm,
            AnagramsForm,
            AnagramsPreview,
            TextsPreview,
            TextsForm,
            LettersFieldPreview,
            LettersFieldForm,
            CatchWordReadingEnForm,
            CatchWordReadingEnPreview,
            CatchWordPreview,
            CatchWordForm,
            VisionFieldForm,
            VisionFieldPreview,
            VisionFieldReadingEnForm,
            VisionFieldReadingEnPreview,
            MatrixForm,
            MatrixPreview,
            MemorizeForm,
            MemorizePreview,
            KeenEyeForm,
            KeenEyePreview,
            KeenEyeBonusGamesForm,
            KeenEyeBonusGamesPreview,
            NumericalNetworkForm,
            NumericalNetworkPreview,
            NavigatorForm,
            NavigatorPreview,
            NavigatorBonusGamesForm,
            NavigatorBonusGamesPreview,
            SynonymsForm,
            SynonymsPreview,
            SynonymsReadingEnForm,
            SynonymsReadingEnPreview,
            SynonymsRussianLanguageForm,
            SynonymsRussianLanguagePreview,
            DiadaForm,
            DiadaPreview,
            AntonymsForm,
            AntonymsPreview,
            AntonymsRussianLanguageForm,
            AntonymsRussianLanguagePreview,
            ClassificationsForm,
            ClassificationsPreview,
            SoundCouplesForm,
            SoundCouplesPreview,
            FourthOneForm,
            FourthOnePreview,
            SearchForm,
            SearchPreview,
            EquationOnlinePreview,
            EquationOnlineForm,
            DigitCouplesPreview,
            DigitCouplesForm,
            MemoryForm,
            MemoryPreview,
            DayOfWeekForm,
            DayOfWeekPreview,
            LonerPreview,
            LonerForm,
            LonerBonusGamesForm,
            LonerBonusGamesPreview,
            CouplesMemoryDevelopmentForm,
            CouplesMemoryDevelopmentPreview,
            MemoForm,
            MemoPreview,
            MemoBonusGamesForm,
            MemoBonusGamesPreview,
            MemoryXForm,
            MemoryXPreview,
            RevivedNumbersForm,
            RevivedNumbersPreview,
            AssociationsForm,
            AssociationsPreview,
            WhoIsWhoForm,
            WhoIsWhoPreview,
            SecretsOfCiceroForm,
            SecretsOfCiceroPreview,
            VocabularyWordsForm,
            VocabularyWordsPreview,
            VocabularyMemoForm,
            VocabularyMemoPreview,
            CosmoTourForm,
            CosmoTourPreview,
            RepeatAfterMeForm,
            RepeatAfterMePreview,
            ChameleonForm,
            ChameleonPreview,
            MentalDrawingForm,
            MentalDrawingPreview,
            HandsUpForm,
            HandsUpPreview,
            CorrectiveSamplesForm,
            CorrectiveSamplesPreview,
            ChainsBonusGameForm,
            ChainsBonusGamePreview,
            ScoutForm,
            ScoutPreview,
            AlpariForm,
            AlpariPreview,
            ConnectThePuzzlesForm,
            ConnectThePuzzlesPreview,
            ChainsReadingEnForm,
            ChainsReadingEnPreview,
            CouplesReadingEnForm,
            CouplesReadingEnPreview,
            TransferSyllablesForm,
            TransferSyllablesPreview,
            TransferSyllablesSchoolPreparingForm,
            TransferSyllablesSchoolPreparingPreview,
            HardSoftConsonantsForm,
            HardSoftConsonantsPreview,
            HardSoftConsonantsSchoolPreparingForm,
            HardSoftConsonantsSchoolPreparingPreview,
            VocabularyMemoRusForm,
            VocabularyMemoRusPreview,
            WordDrumForm,
            WordDrumPreview,
            OrthographForm,
            OrthographPreview,
            TranscriptionInWordForm,
            TranscriptionInWordPreview,
            AlphabetForm,
            AlphabetPreview,
            DivideItRightForm,
            DivideItRightPreview,
            ChooseItRightForm,
            ChooseItRightPreview,
            DivideUnstressedVowelsForm,
            DivideUnstressedVowelsPreview,
            WordStructureForm,
            WordStructurePreview,
            SentenceParsingForm,
            SentenceParsingPreview,
            VocabularyWordsRusForm,
            VocabularyWordsRusPreview,
            LettersSoundsSyllablesForm,
            LettersSoundsSyllablesPreview,
            VoicedAndDeafConsonantsForm,
            VoicedAndDeafConsonantsPreview,
            PartsOfSpeechForm,
            PartsOfSpeechPreview,
            ParsingSentencePartsSpeechForm,
            ParsingSentencePartsSpeechPreview,
            GenericWordsForm,
            GenericWordsPreview,
            VerbTensesForm,
            VerbTensesPreview,
            SortOneManyForm,
            SortOneManyPreview,
            PunctuationMarksForm,
            PunctuationMarksPreview,
        },
    };
</script>

<style scoped lang="scss">
    .form-container {
        display: flex;
        justify-content: center;
        margin: 0;
        height: 100%;
        background-color: $x-color-white;
        border-radius: $border-radius-40;
        box-sizing: border-box;
        @include media($sm) {
            padding: 30px;
        }
    }
    .box {
        margin: 0;
        padding: 30px;
        height: 100%;
        width: 100%;
        background-color: $hsl-color-gray-98;
        box-sizing: border-box;
        border-radius: $border-radius-20;
        &__select {
            width: 100%;
            max-width: 300px;
            margin-bottom: 20px;
            @media screen and (max-width: 767px) {
                max-width: none;
            }
        }
        &__individual-settings-content {
            width: 100%;
            display: flex;
            @media screen and (max-width: 767px) {
                flex-direction: column;
            }
        }
        &__buttons {
            margin-left: 15px;
            @media screen and (max-width: 767px) {
                margin: 0;
            }
        }
        &__individual-parameters {
            margin-bottom: 30px;
            @media screen and (max-width: 767px) {
                margin: 20px 0 30px 0;
            }
        }
    }
    .settings-header {
        @include stack-space($space-x-small);
    }
    .remove-individual-settings-button {
        width: 45px;
        height: 45px;
        padding: 0 !important;
        border-radius: 50% !important;
        margin-left: 5px;
    }
</style>
